import AppPanel from '../fw/AppPanel.react.js';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import DetailToolBar from '../fw/DetailToolBar.react.js';
import DetailBox from '../fw/DetailBox.react.js';
import TableDetail from '../fw/TableDetail.react.js';
import {wrapNotFound} from '../app/NotFoundHandler.react.js';
import filesaver from 'filesaver.js';
import LoadingButton from '../fw/LoadingButton.react';
import {injectIntl, FormattedMessage, defineMessages, FormattedDate, FormattedTime, IntlShape} from 'react-intl';
import Link from 'react-router/lib/Link';

import urlMapping from '../app/URLMapping.js';
import types from '../../common/model-api/types.js';
import ExportIncomingEmailMutation from './ExportIncomingEmailMutation.react.js';
import mutationError from '../channel-defs/mutationError';
import RetryIncomingEmailsMutation from '../flow-defs/RetryIncomingEmailsMutation.react';
import {showJobNotification} from '../app/notification';
import ConfirmDialog from '../fw/ConfirmDialog.react';

import * as BrowserPermission from '../BrowserPermission';
import * as Permission from '../../common/util/Permission';
import { User } from '../../common/types/types';

const messages = defineMessages({
	retryIncomingEmailsButton: {id: 'FlowIncomingEmailView.retryIncomingEmailsButton', 'defaultMessage': 'Retry recevied e-mail'},
	retryIncomingEmailsDescription: {id: 'FlowIncomingEmailView.retryIncomingEmailsDescription', 'defaultMessage': 'Do you really want to retry received e-mail?'},
    viewProcessingAttempts: {id: 'FlowIncomingEmailView.viewProcessingAttempts', 'defaultMessage': 'Show processing attempts'},
});

interface FlowIncomingEmailViewProps {
    intl: IntlShape;
    relay?: object;
    user: User;
}

class FlowIncomingEmailView extends React.Component<FlowIncomingEmailViewProps> {

    typeName = 'flowIncomingEmails';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    constructor() {
        super();
    }

    onClickExport = () => {
        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                let blob = new Blob([atob(response.exportIncomingEmail.output)], {type: response.exportIncomingEmail.contentType});
                filesaver.saveAs(blob, response.exportIncomingEmail.filename, true);
                resolve();
            };

            this.props.relay.commitUpdate(
                new ExportIncomingEmailMutation({
                    flowIncomingEmail: this.props.user.organisation.flow.flowIncomingEmail,
                }), {onSuccess: onSuccess, onFailure: (e) => {
                    mutationError(e);
                    reject(e);
                }}
            );
        });
    };

    stopIncomingEmails() {
        let { user } = this.props;

		return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                if (response.retryIncomingEmails.job) {
                    showJobNotification(this.props.intl, response.retryIncomingEmails.job);
                }

                resolve();
            };

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
                    organisation_id: user.organisation.realId,
                    flow_id: user.organisation.flow.realId,
					flowIncomingEmail_id: user.organisation.flow.flowIncomingEmail.realId,
                    markAsSuccess: true,
				}), {onSuccess: onSuccess, onFailure: reject}
			);
		});
	}


    onClickRetryIncomingEmails() {
		return new Promise((resolve, reject) => {
			let confirmDialogConfig  = {
				message: this.props.intl.formatMessage(messages.retryIncomingEmailsDescription),
				confirm: this.props.intl.formatMessage(messages.retryIncomingEmailsButton),
				confirmStyle: 'btn-danger'
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
				return this.retryIncomingEmails().then(() => {
					return resolve();
				});
			}).catch(() => {
				resolve();
			});
		});
	}




    retryIncomingEmails() {
        let { user } = this.props;

		return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                if (response.retryIncomingEmails.job) {
                    showJobNotification(this.props.intl, response.retryIncomingEmails.job);
                }

                resolve();
            };

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
                    organisation_id: user.organisation.realId,
                    flow_id: user.organisation.flow.realId,
					flowIncomingEmail_id: user.organisation.flow.flowIncomingEmail.realId,
				}), {onSuccess: onSuccess, onFailure: reject}
			);
		});
	}

    render() {
        const data = this.props.user.organisation.flow.flowIncomingEmail;
        let flow = this.props.user.organisation.flow;
        let type = types[this.typeName];

        let columns = {
            'createdAt': {},
            'emailSubject': {},
            'emailSender': {},
            'emailRecepient': {},
            'processed': {},
            'failed': {},
            'firstProcessedAt': {
                render: (column, item) => {
                    let content = data[column] ? <span><FormattedDate value={data[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={data[column]}/></span> : "?";
                    if (data['firstFlowInvocation']) {
                        return <Link to={urlMapping.getUrl(types['flowInvocations'], 'view', data['firstFlowInvocation'])}>{content}</Link>;
                    }

                    return content;
                }
            },
            'lastProcessedAt': {
                render: (column, item) => {
                    let content = data[column] ? <span><FormattedDate value={data[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={data[column]}/></span> : "?";
                    if (data['lastFlowInvocation']) {
                        return <Link to={urlMapping.getUrl(types['flowInvocations'], 'view', data['lastFlowInvocation'])}>{content}</Link>;
                    }

                    return content;
                }
            },
            'nextRetry': {},
        };

        if (data['firstProcessedAt'] === data['lastProcessedAt']) {
            delete columns['firstProcessedAt'];
        }

        return (
            <AppPanel breadcrumbParams={[
                {type: types['organisations'], data: this.props.user.organisation},
                {type: types['flows'], data: this.props.user.organisation.flow},
                {type, data},
            ]} title={data.key}>
                <DetailBox data={data} metadata={type} urlMapping={urlMapping}>
                    <DetailToolBar data={data} metadata={type} urlMapping={urlMapping}>
                        <LoadingButton className="btn btn-default" onClick={() => this.onClickExport()}><span className="fa fa-download"/> <FormattedMessage defaultMessage="Download email" id="flowIncomingEmail.export" /></LoadingButton>
                        <Link className="btn btn-default" to={urlMapping.getUrl(types['flowInvocations'], 'list-email', {
                            organisation_id: data.organisation_id,
                            flow_id: data.flow_id,
                            flowIncomingEmailId: data.realId || data.id,
                        })}><span className="fa fa-history"/> <FormattedMessage {...messages.viewProcessingAttempts} /></Link>

                        {BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled() ?
                                <LoadingButton className="btn btn-default" disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked} icon="fa-refresh" key='incoming-mails' onClick={() => { this.onClickRetryIncomingEmails(); }} ><i className="fa fa-fw fa-envelope"></i> <FormattedMessage defaultMessage="Retry incoming email" id="FlowIncomingEmailView.retryIncomingEmails" /></LoadingButton>
                                : undefined}
                        {BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled() && (data.failed || !data.processed)?
                                <LoadingButton className="btn btn-default" disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked} icon="fa-stop" key='stop-incoming-mails' onClick={() => { this.stopIncomingEmails(); }} ><i className="fa fa-fw fa-stop"></i> <FormattedMessage defaultMessage="Stop incoming email" id="FlowIncomingEmailView.stopIncomingEmails" /></LoadingButton>
                                : undefined}

                    </DetailToolBar>

                    <TableDetail columns={columns} data={data} metadata={type} />
                </DetailBox>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowIncomingEmailView), (props) => {
    return props.user.organisation && props.user.organisation.flow && props.user.organisation.flow.flowIncomingEmail;
}), {
    initialVariables: {
        organisationId: '',
        flowId: '',
        flowIncomingEmailId: '',
    },
    prepareVariables (variables) {
        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    blocked,
                    flow(id: $flowId) {
                        id,
                        realId,
                        organisation_id,
                        paused,
                        enabled,
                        name,
                        flowDef {
                            id,
                            realId,
                            globallyDisabled
                        },
                        flowIncomingEmail(id: $flowIncomingEmailId) {
                            flow_id,
                            organisation_id,
                            id,
                            realId,
                            emailSubject,
                            emailSender,
                            emailRecepient,
                            processed,
                            failed,
                            nextRetry,
                            attempt,
                            firstProcessedAt,
                            firstFlowInvocation {
                                id,
                                realId,
                                organisation_id,
                                flow_id,
                            },
                            lastProcessedAt,
                            lastFlowInvocation {
                                id,
                                realId,
                                organisation_id,
                                flow_id,
                            },
                            createdAt,
                        }
                    }
                }
            }
        `,
    },
});
